import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import type { TaskCardType } from '@econtractor/types';
import * as apiTask from 'api/apiTask';
import AppDatePicker from 'components/dateTime/AppDatePicker';
import AppTimePicker from 'components/dateTime/AppTimePicker';
import { useToast } from 'context/ToastContext';
import spinnerTitle from 'components/others/spinnerTitle';
import { isoDateToEndMillis, millisToIsoDate } from '../NewTask/helpers/initNewTaskStruct';
import { dueDatesFromTask } from './helpers/taskDueDateHelpers';

interface TaskExecutorTermFormProps {
    task: TaskCardType;
    onCancel: () => void;
    onSaved: (task: TaskCardType) => void;
}

const TaskExecutorTermForm = ({ task, onCancel, onSaved }: TaskExecutorTermFormProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const initial = dueDatesFromTask(task);
    const [dateIso, setDateIso] = useState(
        () => millisToIsoDate(task.dateEnd) || DT.now().setZone('local').toISODate() || '',
    );
    const [withTime, setWithTime] = useState(Boolean(task.withTime));
    const [endTime, setEndTime] = useState(initial.endTime || '18:00');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    const anchorIso = millisToIsoDate(task.dateStart) || millisToIsoDate(task.dateEnd);
    const spansDays = Boolean(anchorIso && dateIso > anchorIso);
    const timeSwitchId = `task-${task.ID}-executor-term-time`;

    const handleSave = async () => {
        const commentText = comment.trim();
        if (!commentText) {
            showToast(t('errEmptyTermComment'), 'danger');
            return;
        }

        const dateEnd = isoDateToEndMillis(dateIso, withTime, endTime);
        if (!dateEnd) {
            showToast(t('errExecutorTermBeforeStart'), 'danger');
            return;
        }

        setSaving(true);
        const result = await apiTask.updateExecutorTerm(task.ID, {
            dateEnd,
            withTime,
            endTime: withTime ? endTime : null,
            comment: commentText,
        });
        setSaving(false);

        if (!result?.stat || !result.task) {
            if (result?.errCode === 'EXECUTOR_TIME_BUSY') {
                showToast(t('errOwnTimeBusy'), 'danger');
                return;
            }
            if (result?.errCode === 'EMPTY_COMMENT') {
                showToast(t('errEmptyTermComment'), 'danger');
                return;
            }
            if (result?.errCode === 'INVALID_DATES') {
                showToast(t('errExecutorTermBeforeStart'), 'danger');
                return;
            }
            showToast(t('common:messError'), 'danger');
            return;
        }

        showToast(t('messExecutorTermUpdated'), 'success');
        onSaved(result.task);
    };

    return (
        <div className="mt-2">
            <Form.Group className="mb-3">
                {spansDays ? (
                    <div className="d-flex gap-2">
                        <div className="flex-fill">
                            <label className="form-label text-muted">{t('labelDateStart')}</label>
                            <AppDatePicker
                                value={anchorIso}
                                disabled
                                onChange={() => undefined}
                            />
                        </div>
                        <div className="flex-fill">
                            <label className="form-label text-muted">{t('labelDateEnd')}</label>
                            <div className="d-flex gap-2">
                                <AppDatePicker
                                    value={dateIso}
                                    min={anchorIso || undefined}
                                    disabled={saving}
                                    style={{ minWidth: 0, flex: '1 1 0' }}
                                    onChange={(iso) => {
                                        if (!iso) return;
                                        setDateIso(iso);
                                    }}
                                />
                                {withTime ? (
                                    <AppTimePicker
                                        value={endTime || '18:00'}
                                        disabled={saving}
                                        style={{ minWidth: 0, flex: '1 1 0' }}
                                        onChange={(time) => {
                                            if (!time) return;
                                            setEndTime(time);
                                        }}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <label className="form-label text-muted">{t('labelDateEnd')}</label>
                        <div className="d-flex gap-2">
                            <AppDatePicker
                                value={dateIso}
                                min={anchorIso || undefined}
                                disabled={saving}
                                style={{ minWidth: 0, flex: '1 1 0' }}
                                onChange={(iso) => {
                                    if (!iso) return;
                                    setDateIso(iso);
                                }}
                            />
                            {withTime ? (
                                <AppTimePicker
                                    value={endTime || '18:00'}
                                    disabled={saving}
                                    style={{ minWidth: 0, flex: '1 1 0' }}
                                    onChange={(time) => {
                                        if (!time) return;
                                        setEndTime(time);
                                    }}
                                />
                            ) : null}
                        </div>
                    </>
                )}
            </Form.Group>

            <div className="form-check form-switch mb-3">
                <input
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    id={timeSwitchId}
                    checked={withTime}
                    disabled={saving}
                    onChange={(e) => setWithTime(e.target.checked)}
                />
                <label className="form-check-label" htmlFor={timeSwitchId}>
                    {t('labelWithTime')}
                </label>
            </div>

            <Form.Group className="mb-3">
                <label className="form-label text-muted">{t('labelTermChangeComment')}</label>
                <textarea
                    className="form-control"
                    rows={3}
                    required
                    value={comment}
                    disabled={saving}
                    onChange={(e) => setComment(e.target.value)}
                />
            </Form.Group>

            <div className="text-end">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mb-1 min70px"
                    style={{ height: 24 }}
                    disabled={saving}
                    onClick={onCancel}
                >
                    {t('common:buttons.cancel')}
                </Button>
                <Button
                    type="button"
                    variant="success"
                    size="sm"
                    className="mb-1 ms-2 min70px"
                    style={{ height: 24 }}
                    disabled={saving}
                    onClick={() => void handleSave()}
                >
                    {spinnerTitle(t('common:buttons.change'), saving, t('common:buttons.saving'))}
                </Button>
            </div>
        </div>
    );
};

export default TaskExecutorTermForm;
